
import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface RemoveFromBoardButtonProps {
  className?: string;
}

interface DragData {
  contentId?: string;
  contentIds?: string[];
  contentTitle?: string;
  contentTitles?: string;
  currentCollectionId?: string;
  isMultiDrag?: boolean;
}

const RemoveFromBoardButton = ({ className }: RemoveFromBoardButtonProps) => {
  const { id: boardId } = useParams<{ id: string }>();
  const [isRemoving, setIsRemoving] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const queryClient = useQueryClient();

  // This function will be called when items are dropped on the remove zone
  const handleRemoveItemFromBoard = async (contentId: string) => {
    if (!boardId || !contentId) return;
    
    try {
      const { error } = await supabase
        .from('content_boards')
        .delete()
        .eq('board_id', boardId)
        .eq('content_id', contentId);
        
      if (error) throw error;
      
      console.log(`Successfully removed content ${contentId} from board ${boardId}`);
    } catch (error) {
      console.error("Error removing item from board:", error);
      throw error;
    }
  };

  // Handle multiple items removal
  const handleRemoveMultipleItems = async (contentIds: string[]) => {
    if (!boardId || !contentIds.length) return;
    
    try {
      // Remove all items in parallel
      const removePromises = contentIds.map(contentId => 
        handleRemoveItemFromBoard(contentId)
      );
      
      await Promise.all(removePromises);
      
      console.log(`Successfully removed ${contentIds.length} items from board ${boardId}`);
    } catch (error) {
      console.error("Error removing multiple items from board:", error);
      throw error;
    }
  };

  // Handle drag over event
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDropTarget(true);
  };

  // Handle drag leave event
  const handleDragLeave = () => {
    setIsDropTarget(false);
  };

  // Handle drop event
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDropTarget(false);
    
    if (isRemoving) return; // Prevent multiple concurrent operations
    
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (!jsonData) {
        console.error('No valid JSON data found in drag event');
        toast.error("Couldn't process the dropped item");
        return;
      }
      
      const data: DragData = JSON.parse(jsonData);
      
      setIsRemoving(true);
      
      // Handle multi-drag case
      if (data.isMultiDrag && data.contentIds && data.contentIds.length > 0) {
        console.log(`Multiple items dropped for removal: ${data.contentIds.length} items`);
        
        await handleRemoveMultipleItems(data.contentIds);
        
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['relatedContent'] });
        queryClient.invalidateQueries({ queryKey: ['boardContent'] });
        
        toast.success(`${data.contentIds.length} items removed from board`);
        return;
      }
      
      // Single item case
      if (!data.contentId) {
        console.error('Invalid content data in drag operation');
        toast.error("Missing content information");
        return;
      }
      
      console.log(`Content dropped for removal: ${data.contentTitle || 'Unknown'} (${data.contentId})`);
      
      await handleRemoveItemFromBoard(data.contentId);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['relatedContent'] });
      queryClient.invalidateQueries({ queryKey: ['boardContent'] });
      
      toast.success("Item removed from board");
      
    } catch (error) {
      console.error('Error processing dropped content:', error);
      toast.error("Failed to remove item(s) from board");
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div
      className={cn(
        "flex items-center",
        className
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Button
        variant="outline"
        disabled={isRemoving}
        className={cn(
          "text-orange-500 border-2 border-orange-300 hover:bg-orange-50 hover:text-orange-600 flex items-center gap-2 transition-colors",
          isDropTarget && "bg-orange-100 border-orange-400",
          isRemoving && "opacity-50 cursor-not-allowed"
        )}
      >
        <X size={18} />
        <span>{isRemoving ? "Removing..." : "Remove"}</span>
      </Button>
    </div>
  );
};

export default RemoveFromBoardButton;
